/**
 * The Saf3AI Agent canvas: pure helpers for pinning chat answers to an AI
 * dashboard and laying the tiles out on a 12-column grid. No React, no API.
 *
 * A canvas IS an AI dashboard (`/ai-explorer/dashboards/:id`); a tile is one
 * of its widgets with three extra fields: where it came from
 * (`conversation_id`, `message_id`), what it is (`kind`: an answer, or a
 * sentence pinned as an `insight`) and where it sits (`layout`).
 */
import { tableFromMessage } from './agentModel';

export const GRID_COLS = 12;
export const ROW_PX = 44;
export const GAP_PX = 12;
export const MAX_ROWS = 40;
export const ACTIVE_CANVAS_KEY = 'saf3ai.agent.canvas';

const SIZES = {
  chart: { w: 6, h: 7 },
  table: { w: 12, h: 6 },
  stat: { w: 3, h: 3 },
  text: { w: 6, h: 5 },
  insight: { w: 6, h: 3 },
  note: { w: 4, h: 3 },
  section: { w: 12, h: 1 },
  summary: { w: 12, h: 3 },
};

/** The size a new tile starts at, by what it shows. */
export const tileSize = (widget) => SIZES[['insight', 'note', 'section', 'summary'].includes(widget?.kind) ? widget.kind : widget?.display_type] || SIZES.text;

const int = (v, d) => (Number.isFinite(Number(v)) ? Math.round(Number(v)) : d);

/** A rectangle forced onto the grid: at least 1×1, never past the right edge. */
export const clampRect = (r) => {
  const w = Math.min(GRID_COLS, Math.max(1, int(r.w, 6)));
  const h = Math.min(MAX_ROWS, Math.max(1, int(r.h, 4)));
  return { x: Math.min(GRID_COLS - w, Math.max(0, int(r.x, 0))), y: Math.max(0, int(r.y, 0)), w, h };
};

export const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** First row past every tile. */
export const gridBottom = (rects) => rects.reduce((m, r) => Math.max(m, r.y + r.h), 0);

/** The first free spot, reading left to right then top to bottom. */
export const firstFit = (rects, { w, h }) => {
  const size = clampRect({ x: 0, y: 0, w, h });
  const last = gridBottom(rects);
  for (let y = 0; y <= last; y += 1) {
    for (let x = 0; x <= GRID_COLS - size.w; x += 1) {
      const cand = { ...size, x, y };
      if (!rects.some((r) => overlaps(r, cand))) return cand;
    }
  }
  return { ...size, x: 0, y: last };
};

const valid = (l) => l && ['x', 'y', 'w', 'h'].every((k) => Number.isFinite(Number(l[k])));

/**
 * Every widget with a layout. Tiles saved without one (dashboards made before
 * the canvas, or by the generator) are placed in their saved order after the
 * ones that have a layout.
 */
export const withLayouts = (widgets = []) => {
  const list = Array.isArray(widgets) ? widgets : [];
  const placed = list.filter((w) => valid(w.layout)).map((w) => ({ ...w, layout: clampRect(w.layout) }));
  const rects = placed.map((w) => w.layout);
  const rest = list.filter((w) => !valid(w.layout))
    .sort((a, b) => int(a.position, 0) - int(b.position, 0))
    .map((w) => {
      const layout = firstFit(rects, tileSize(w));
      rects.push(layout);
      return { ...w, layout };
    });
  const byId = new Map([...placed, ...rest].map((w) => [w.id, w]));
  return list.map((w) => byId.get(w.id));
};

/** Pull every tile up as far as it goes, top tiles first. */
export const compact = (widgets) => {
  const order = [...widgets].sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);
  const done = [];
  order.forEach((w) => {
    const l = { ...w.layout };
    while (l.y > 0 && !done.some((d) => overlaps(d.layout, { ...l, y: l.y - 1 }))) l.y -= 1;
    done.push({ ...w, layout: l });
  });
  const byId = new Map(done.map((w) => [w.id, w]));
  return widgets.map((w) => byId.get(w.id));
};

/**
 * Put one tile at `rect` (a move or a resize). Tiles it lands on are pushed
 * down, then the grid is compacted. Order of the list is kept.
 */
export const placeTile = (widgets, id, rect) => {
  const target = widgets.find((w) => w.id === id);
  if (!target) return widgets;
  const moved = { ...target, layout: clampRect(rect) };
  const settled = [moved];
  widgets.filter((w) => w.id !== id)
    .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x)
    .forEach((w) => {
      const l = { ...w.layout };
      while (settled.some((s) => overlaps(s.layout, l))) l.y += 1;
      settled.push({ ...w, layout: l });
    });
  const byId = new Map(settled.map((w) => [w.id, w]));
  return compact(widgets.map((w) => byId.get(w.id)));
};

/** Tiles in reading order: top to bottom, then left to right. */
export const readingOrder = (widgets) => [...widgets].sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);

const kindOf = (w) => (w.kind === 'insight' || w.kind === 'summary' ? 'insight' : w.display_type || 'text');

/**
 * The three layouts the Arrange menu offers. Pure placement rules — no model
 * call — applied in reading order (Executive brief groups by kind first):
 * - brief: insights and summary across the top, numbers in a row, charts two
 *   up, tables full width, then the rest.
 * - grid: everything half width (tables full), as dense as it packs.
 * - story: one column, top to bottom, like a report.
 */
export const ARRANGE_STYLES = ['brief', 'grid', 'story'];
const BRIEF_RANK = { insight: 0, stat: 1, chart: 2, table: 3, text: 4 };
const STYLE_SIZE = {
  brief: { insight: { w: 12, h: 3 }, stat: { w: 3, h: 3 }, chart: { w: 6, h: 7 }, table: { w: 12, h: 6 }, text: { w: 6, h: 5 } },
  grid: { insight: { w: 6, h: 3 }, stat: { w: 3, h: 3 }, chart: { w: 6, h: 6 }, table: { w: 12, h: 6 }, text: { w: 6, h: 5 } },
  story: { insight: { w: 12, h: 2 }, stat: { w: 12, h: 2 }, chart: { w: 12, h: 7 }, table: { w: 12, h: 6 }, text: { w: 12, h: 4 } },
};
export const arrange = (widgets, style) => {
  const sizes = STYLE_SIZE[style];
  if (!sizes) return widgets;
  let order = readingOrder(widgets);
  if (style === 'brief') {
    order = order.map((w, i) => ({ w, i }))
      .sort((a, b) => (BRIEF_RANK[kindOf(a.w)] ?? 9) - (BRIEF_RANK[kindOf(b.w)] ?? 9) || a.i - b.i)
      .map((x) => x.w);
  }
  const placed = new Map();
  if (style === 'grid') {
    const rects = [];
    order.forEach((w) => {
      const layout = firstFit(rects, sizes[kindOf(w)] || sizes.text);
      rects.push(layout);
      placed.set(w.id, { ...w, layout });
    });
  } else {
    // Brief and story read top down: fill a row left to right, then start the
    // next. In a brief each kind (insights, numbers, charts…) starts its own row.
    let x = 0; let y = 0; let rowH = 0; let prev = null;
    order.forEach((w) => {
      const size = sizes[kindOf(w)] || sizes.text;
      const newKind = style === 'brief' && prev !== null && kindOf(w) !== prev;
      if (x > 0 && (x + size.w > GRID_COLS || newKind)) { y += rowH; x = 0; rowH = 0; }
      prev = kindOf(w);
      placed.set(w.id, { ...w, layout: { x, y, w: size.w, h: size.h } });
      x += size.w; rowH = Math.max(rowH, size.h);
    });
  }
  return widgets.map((w) => placed.get(w.id));
};

/** Arrow-key handling for a focused tile: arrows move, Shift+arrows resize. */
export const nudge = (layout, key, resize) => {
  const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[key];
  if (!d) return null;
  return resize
    ? clampRect({ ...layout, w: layout.w + d[0], h: layout.h + d[1] })
    : clampRect({ ...layout, x: layout.x + d[0], y: layout.y + d[1] });
};

/** Pixel box for a grid rectangle, given one column's width. */
export const rectToPx = (r, colPx) => ({
  left: r.x * (colPx + GAP_PX),
  top: r.y * (ROW_PX + GAP_PX),
  width: r.w * colPx + (r.w - 1) * GAP_PX,
  height: r.h * ROW_PX + (r.h - 1) * GAP_PX,
});

/** Grid cells covered by a pointer drag of (dx, dy) pixels. */
export const pxToCells = (dx, dy, colPx) => ({
  cols: Math.round(dx / (colPx + GAP_PX)),
  rows: Math.round(dy / (ROW_PX + GAP_PX)),
});

/** What only the layout save sends back: the widget, minus nothing it needs. */
export const forSave = (w) => ({
  ...w,
  title: String(w.title || ''),
  query: String(w.query || w.title || ''),
  layout: clampRect(w.layout || {}),
});

/**
 * The widget an answer becomes when pinned. A chart keeps its series, a table
 * its rows, anything else its text. The question is the tile's title and
 * stays with it so the tile can be traced back to the chat.
 */
/** A tile title from a question: whole words, with … when it had to be cut (as the server does). */
export const shortTitle = (text, limit = 80) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  const cut = clean.slice(0, limit - 1).replace(/\s+\S*$/, '').replace(/[,;:-]+$/, '');
  return `${cut || clean.slice(0, limit - 1)}…`;
};

export const widgetFromMessage = (m, { question = '', conversationId = null, untitled = 'Untitled' } = {}) => {
  const isChart = m.displayType === 'chart' && m.data && Array.isArray(m.data.labels);
  const table = !isChart && m.displayType === 'table' ? tableFromMessage(m) : null;
  const q = String(question || '').trim();
  return {
    title: shortTitle(q) || untitled,
    query: q || untitled,
    display_type: isChart ? 'chart' : table ? 'table' : 'text',
    chart_type: isChart ? (m.chartType || 'bar') : null,
    data: isChart ? m.data : table || { text: String(m.content || '').slice(0, 10000) },
    size: 'medium',
    kind: 'answer',
    message_id: m.id || null,
    conversation_id: conversationId || null,
  };
};

/** A sentence highlighted in an answer, pinned as an insight. */
export const insightWidget = (text, { question = '', conversationId = null, messageId = null } = {}) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim().slice(0, 2000);
  return {
    title: clean.slice(0, 80),
    query: String(question || '').trim() || clean.slice(0, 200),
    display_type: 'text',
    chart_type: null,
    data: { text: clean },
    size: 'medium',
    kind: 'insight',
    message_id: messageId,
    conversation_id: conversationId || null,
  };
};

// ── Show as: presentation only; the tile's data is never changed ──────────

const CHART_TYPES = ['bar', 'line', 'pie', 'doughnut'];
const numeric = (v) => v !== null && v !== '' && Number.isFinite(Number(String(v).replace(/[,%]/g, '')));

/** The first table column (after the label column) whose cells are all numbers. */
const numericColumn = (data) => {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  if (!rows.length) return -1;
  const width = Math.max(...rows.map((r) => (Array.isArray(r) ? r.length : 0)));
  for (let j = 1; j < width; j += 1) if (rows.every((r) => numeric(r?.[j]))) return j;
  return -1;
};

/** The ways a tile can be shown: a chart as other charts or a table, a table as a chart when it has numbers. */
export const viewOptions = (w) => {
  if (!w || ['insight', 'note', 'section', 'summary'].includes(w.kind)) return [];
  if (w.display_type === 'chart' && Array.isArray(w.data?.labels)) return [...CHART_TYPES, 'table'];
  if (w.display_type === 'table' && numericColumn(w.data) > 0) return ['table', ...CHART_TYPES];
  return [];
};

/** The tile as the viewer chose to show it ({display_type, chart_type, data}). */
export const applyView = (w) => {
  const want = w?.view_as;
  if (!want || !viewOptions(w).includes(want)) return w;
  if (w.display_type === 'chart') {
    if (want !== 'table') return { ...w, chart_type: want };
    const values = Array.isArray(w.data.values) ? w.data.values : (w.data.datasets?.[0]?.data || []);
    return { ...w, display_type: 'table', chart_type: null, data: { headers: ['', w.data.datasets?.[0]?.label || ''], rows: w.data.labels.map((l, i) => [l, values[i] ?? '']) } };
  }
  if (want === 'table') return w;
  const j = numericColumn(w.data);
  return {
    ...w,
    display_type: 'chart',
    chart_type: want,
    data: { labels: w.data.rows.map((r) => String(r[0])), values: w.data.rows.map((r) => Number(String(r[j]).replace(/[,%]/g, ''))) },
  };
};

/** A tile the server redraws with no model call (vs. a saved answer). */
export const isLive = (w) => w?.spec?.mode === 'live';

/** What a tile shows right now: a filtered or viewer redraw when there is one. */
export const shownAs = (w, view) => (view && !view.error && view.display_type
  ? { ...w, display_type: view.display_type, chart_type: view.chart_type, data: view.data }
  : w);

/** The answer tile already pinned from this message, if any. */
export const pinnedAnswer = (canvas, messageId) => (canvas?.widgets || [])
  .find((w) => messageId && w.message_id === messageId && w.kind !== 'insight') || null;

/** Text a selection must have to be pinned: something, but not a whole page. */
export const pinnableSelection = (text) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length >= 3 && clean.length <= 2000 ? clean : '';
};

export const readActiveCanvas = () => {
  try { return localStorage.getItem(ACTIVE_CANVAS_KEY) || null; } catch { return null; }
};

export const writeActiveCanvas = (id) => {
  try {
    if (id) localStorage.setItem(ACTIVE_CANVAS_KEY, id);
    else localStorage.removeItem(ACTIVE_CANVAS_KEY);
  } catch { /* private mode */ }
};
